/* Copy da tela de detalhe de pack (ADR-0703): cabeçalho, métricas, abas e diálogo de takedown. */
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

interface PackDetailContent {
  readonly breadcrumbAriaLabel: string;
  readonly catalogBreadcrumb: string;
  readonly packFallbackLabel: string;
  readonly loadError: string;
  readonly verifiedBadge: string;
  readonly notVerifiedBadge: string;
  readonly packIdLabel: string;
  readonly noActiveVersionBadge: string;
  readonly metrics: {
    readonly installs: string;
    readonly activeVersion: string;
    readonly allYankedHint: string;
    readonly versions: string;
  };
  readonly tabs: {
    readonly overview: string;
    readonly versions: string;
    readonly takedowns: string;
  };
  readonly overviewCardTitle: string;
  readonly fields: {
    readonly author: string;
    readonly kind: string;
    readonly tier: string;
    readonly visibility: string;
    readonly verified: string;
    readonly updatedAt: string;
  };
  readonly allowedOrgsCardTitle: string;
  readonly noAllowedOrgs: string;
  readonly takedownDialog: {
    /** Título dinâmico: nome do pack + versão fazem parte do texto, não só interpolação de dado. */
    readonly title: (packName: string, version: string) => string;
    readonly description: string;
    readonly confirmLabel: string;
    readonly reasonLabel: string;
    readonly successToast: string;
    readonly errorToast: string;
  };
}

const PACK_DETAIL_CONTENT: Bilingual<PackDetailContent> = {
  'pt-BR': {
    breadcrumbAriaLabel: 'Trilha',
    catalogBreadcrumb: 'Catálogo',
    packFallbackLabel: 'Pack',
    loadError: 'Não foi possível carregar o detalhe do pack.',
    verifiedBadge: 'Verificado',
    notVerifiedBadge: 'Não verificado',
    packIdLabel: 'ID do pack',
    noActiveVersionBadge: 'Sem versão ativa',
    metrics: {
      installs: 'Instalações',
      activeVersion: 'Versão ativa',
      allYankedHint: 'todas yanked/deprecated',
      versions: 'Versões',
    },
    tabs: {
      overview: 'Visão geral',
      versions: 'Versões',
      takedowns: 'Histórico de takedown',
    },
    overviewCardTitle: 'Visão geral',
    fields: {
      author: 'Autor',
      kind: 'Tipo',
      tier: 'Tier',
      visibility: 'Visibilidade',
      verified: 'Verificado',
      updatedAt: 'Atualizado em',
    },
    allowedOrgsCardTitle: 'Orgs autorizadas',
    noAllowedOrgs: 'Nenhuma org autorizada listada no catálogo.',
    takedownDialog: {
      title: (packName, version) => `Takedown de ${packName} v${version}`,
      description:
        'Remove esta versão imediatamente de novos downloads e instalações. Diferente de retirar do catálogo, a indisponibilidade é imediata. Informe o motivo.',
      confirmLabel: 'Fazer takedown',
      reasonLabel: 'Motivo',
      successToast: 'Versão removida (takedown).',
      errorToast: 'Falha ao fazer takedown da versão.',
    },
  },
  'en-US': {
    breadcrumbAriaLabel: 'Breadcrumb',
    catalogBreadcrumb: 'Catalog',
    packFallbackLabel: 'Pack',
    loadError: 'Could not load the pack details.',
    verifiedBadge: 'Verified',
    notVerifiedBadge: 'Not verified',
    packIdLabel: 'Pack ID',
    noActiveVersionBadge: 'No active version',
    metrics: {
      installs: 'Installs',
      activeVersion: 'Active version',
      allYankedHint: 'all yanked/deprecated',
      versions: 'Versions',
    },
    tabs: {
      overview: 'Overview',
      versions: 'Versions',
      takedowns: 'Takedown history',
    },
    overviewCardTitle: 'Overview',
    fields: {
      author: 'Author',
      kind: 'Type',
      tier: 'Tier',
      visibility: 'Visibility',
      verified: 'Verified',
      updatedAt: 'Updated at',
    },
    allowedOrgsCardTitle: 'Authorized orgs',
    noAllowedOrgs: 'No authorized org listed in the catalog.',
    takedownDialog: {
      title: (packName, version) => `Takedown of ${packName} v${version}`,
      description:
        'Immediately removes this version from new downloads and installations. Unlike removing it from the catalog, unavailability is immediate. Enter the reason.',
      confirmLabel: 'Take down',
      reasonLabel: 'Reason',
      successToast: 'Version removed (takedown).',
      errorToast: 'Failed to take down the version.',
    },
  },
};

export const usePackDetailContent = (): PackDetailContent => useBilingual(PACK_DETAIL_CONTENT);
